'use client';

import React, { ChangeEvent, useId, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Caption } from '../Caption/Caption';
import { Radio } from '../Radio/Radio';
import './RadioGroup.css';

export interface RadioGroupItem<TValue> {
  value: TValue;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupProps<TValue> {
  label?: string;
  name?: string;
  items: RadioGroupItem<TValue>[];
  value?: TValue | null;
  defaultValue?: TValue | null;
  onValueChange?: (value: TValue | null) => void;
  required?: boolean;
  disabled?: boolean;
  orientation?: 'vertical' | 'horizontal';
  ariaLabel?: string;
  id?: string;
  caption?: React.ReactNode;
  message?: string;
  messageType?: '' | 'error' | 'success';
}

export const RadioGroup = React.memo(function RadioGroup<TValue>({
  label,
  name,
  items,
  value,
  defaultValue = null,
  onValueChange,
  required = false,
  disabled = false,
  orientation = 'vertical',
  ariaLabel,
  id,
  caption,
  message,
  messageType = '',
}: RadioGroupProps<TValue>) {
  const reactId = useId();
  const groupId = id ?? `radio-group-${reactId}`;
  const groupLabelId = label ? `${groupId}-label` : undefined;
  const captionId = caption ? `${groupId}-caption` : undefined;
  const messageId = message ? `${groupId}-message` : undefined;
  const generatedName = name ?? groupId;

  // Normalize items to include string value for DOM and retain original value
  const normalizedItems = useMemo(
    () => items.map((i) => ({ ...i, sValue: String(i.value) })),
    [items],
  );

  const [internalValue, setInternalValue] = useState<TValue | null>(defaultValue);
  const selectedValue = (value ?? internalValue) as TValue | null;

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const sVal = e.target.value;
    const next = (normalizedItems.find((i) => i.sValue === sVal)?.value ?? null) as TValue | null;
    if (value === undefined) {
      setInternalValue(next);
    }
    onValueChange?.(next);
  };

  return (
    <div
      className="radio-group"
      role="radiogroup"
      aria-labelledby={groupLabelId}
      aria-describedby={[captionId, messageId].filter(Boolean).join(' ') || undefined}
      aria-invalid={messageType === 'error' || undefined}
      data-orientation={orientation}
      data-disabled={disabled}
      id={groupId}
    >
      {label && (
        <span className="radio-group__label" id={groupLabelId}>
          {label}
          {required && <span className="radio-group__required">*</span>}
        </span>
      )}
      {caption && (
        <Caption>
          <span id={captionId}>{caption}</span>
        </Caption>
      )}
      <div className="radio-group__items">
        {normalizedItems.map((item, index) => (
          <div className="radio-group__item" key={item.sValue}>
            <Radio
              label={item.label}
              value={item.sValue}
              name={generatedName}
              required={required && index === 0}
              disabled={disabled || item.disabled}
              checked={selectedValue != null && String(selectedValue) === item.sValue}
              onChange={handleChange}
              ariaLabel={ariaLabel}
            />
          </div>
        ))}
      </div>
      <AnimatePresence initial={false}>
        {message && (
          <motion.div
            key="radio-group-message"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{ overflow: 'hidden' }}
          >
            <span id={messageId} className="radio-group__message" data-type={messageType}>
              {message}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

RadioGroup.displayName = 'RadioGroup';
